import React, { useState, useEffect } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import {
  Sun,
  BatteryCharging,
  Zap,
  Clock,
  ShieldCheck,
  Activity,
  Play,
  Pause,
  RotateCcw,
  Wifi,
  Terminal,
  AlertTriangle,
  Flame,
  CheckCircle2,
  Radio,
  Gauge,
  Waves,
  Power,
  Tv,
  Wind,
  Refrigerator,
  Droplets,
  DollarSign,
  TrendingUp,
  Cpu
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';

interface TelemetryLog {
  id: string;
  timestamp: string;
  type: 'INFO' | 'WARN' | 'SUCCESS' | 'EVENT';
  message: string;
}

interface ApplianceToggle {
  id: string;
  name: string;
  watts: number;
  icon: React.ReactNode;
  active: boolean;
  category: string;
}

export const LivePowerHub: React.FC = () => {
  const {
    timeOfDay,
    setTimeOfDay,
    currentSoc,
    setCurrentSoc,
    getMetrics,
    getActivePreset,
    gridAvailable,
    toggleGrid
  } = useSolarStore();

  const metrics = getMetrics();
  const preset = getActivePreset();

  // Live Auto-Simulation State
  const [isLiveStreaming, setIsLiveStreaming] = useState<boolean>(true);
  const [speedMultiplier, setSpeedMultiplier] = useState<number>(1);
  const [jitter, setJitter] = useState<number>(0);
  const [sinePhase, setSinePhase] = useState<number>(0);
  const [simulatedCloud, setSimulatedCloud] = useState<boolean>(false);
  const [simulatedSurge, setSimulatedSurge] = useState<boolean>(false);
  const [logs, setLogs] = useState<TelemetryLog[]>([]);

  // Telemetry Packet Stats
  const [packetCount, setPacketCount] = useState<number>(2840);
  const [latency, setLatency] = useState<number>(14);
  const [bytesReceived, setBytesReceived] = useState<number>(428500);

  // Live Appliance Switchboard State
  const [appliances, setAppliances] = useState<ApplianceToggle[]>([
    { id: 'ac', name: '1.5HP Inverter AC', watts: 1200, icon: <Wind className="w-4 h-4" />, active: true, category: 'Cooling' },
    { id: 'freezer', name: 'Deep Freezer & Fridge', watts: 380, icon: <Refrigerator className="w-4 h-4" />, active: true, category: 'Kitchen' },
    { id: 'pump', name: '1HP Water Pump', watts: 1100, icon: <Droplets className="w-4 h-4" />, active: false, category: 'Utility' },
    { id: 'tv', name: '65" 4K TV & Soundbar', watts: 180, icon: <Tv className="w-4 h-4" />, active: true, category: 'Entertainment' },
    { id: 'fans', name: 'Standing & Ceiling Fans', watts: 150, icon: <Wind className="w-4 h-4" />, active: true, category: 'Comfort' },
    { id: 'lights', name: 'LED Lights & Security', watts: 90, icon: <Zap className="w-4 h-4" />, active: true, category: 'Lighting' },
  ]);

  // Session Financial Offset Counter (Live Naira Savings)
  const [sessionNairaSaved, setSessionNairaSaved] = useState<number>(1480.50);

  // Active Total Appliance Load Watts
  const activeApplianceWatts = appliances
    .filter((a) => a.active)
    .reduce((sum, a) => sum + a.watts, 0);

  // Live Timer Interval (100ms for smooth oscilloscope sine wave & telemetry stream)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (isLiveStreaming) {
      interval = setInterval(() => {
        // Advance sine wave phase
        setSinePhase((prev) => (prev + 0.25) % (Math.PI * 20));

        // Jitter for live micro-fluctuations
        setJitter((Math.random() - 0.5) * 0.12);

        // Packet counter & byte size increment
        setPacketCount((prev) => prev + 1);
        setBytesReceived((prev) => prev + Math.floor(120 + Math.random() * 80));
        setLatency(Math.floor(12 + Math.random() * 8));

        // Increment Live Financial Savings ticker
        setSessionNairaSaved((prev) => prev + (0.15 * speedMultiplier));

        // Advance time of day
        const nextTime = (timeOfDay + 0.05 * speedMultiplier) % 24;
        setTimeOfDay(Number(nextTime.toFixed(2)));
      }, 200 / speedMultiplier);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLiveStreaming, speedMultiplier, timeOfDay, setTimeOfDay]);

  // Telemetry Log Broadcaster
  const addLog = (message: string, type: 'INFO' | 'WARN' | 'SUCCESS' | 'EVENT' = 'INFO') => {
    const timeStr = new Date().toLocaleTimeString('en-GB');
    setLogs((prev) => [
      { id: Math.random().toString(), timestamp: timeStr, type, message },
      ...prev.slice(0, 9)
    ]);
  };

  // Toggle Appliance Handlers
  const toggleAppliance = (id: string) => {
    setAppliances((prev) =>
      prev.map((a) => {
        if (a.id === id) {
          const newState = !a.active;
          addLog(
            `[SWITCHBOARD] ${a.name} turned ${newState ? 'ON (+ ' + a.watts + 'W)' : 'OFF (- ' + a.watts + 'W)'}. Total Load: ${activeApplianceWatts + (newState ? a.watts : -a.watts)}W.`,
            newState ? 'INFO' : 'EVENT'
          );
          return { ...a, active: newState };
        }
        return a;
      })
    );
  };

  // Log telemetry events when parameters shift
  useEffect(() => {
    if (simulatedCloud) {
      addLog(`[ENVIRONMENT] Cloud shadow detected over panel array. Solar output reduced by 45%.`, 'WARN');
    } else if (simulatedSurge) {
      addLog(`[LOAD_SPIKE] Heavy AC compressor startup surge detected. Hybrid inverter regulating 4,200W load.`, 'WARN');
    } else if (!gridAvailable) {
      addLog(`[GRID_OUTAGE] PHCN Grid Disconnected. 0ms UPS seamless transfer to Lithium Battery storage.`, 'WARN');
    } else {
      addLog(`[TELEMETRY] System Nominal. V_Grid=230.4V, Freq=50.01Hz, Solar=${(metrics.currentPowerGenerationKw + jitter).toFixed(2)}kW, Battery SOC=${currentSoc}%.`, 'SUCCESS');
    }
  }, [simulatedCloud, simulatedSurge, gridAvailable]);

  // Generate 24-hour baseline curve for Recharts
  const generate24HourData = () => {
    const data = [];
    for (let hour = 0; hour <= 24; hour += 1) {
      let solar = 0;
      if (hour >= 6 && hour <= 18) {
        solar = Math.sin(((hour - 6) / 12) * Math.PI) * preset.solarPanelCapacityKw;
        if (simulatedCloud && Math.abs(hour - timeOfDay) < 2) {
          solar *= 0.55;
        }
      }

      const baseLoadKw = activeApplianceWatts / 1000;
      let load = Math.max(0.4, baseLoadKw + (hour >= 18 || hour <= 7 ? 0.3 : 0));
      if (simulatedSurge && Math.abs(hour - timeOfDay) < 1) {
        load += 2.2;
      }

      const isCurrentHour = Math.floor(timeOfDay) === hour;

      data.push({
        time: `${hour}:00`,
        SolarGenerationKw: Number(Math.max(0, solar).toFixed(2)),
        LoadDemandKw: Number(load.toFixed(2)),
        BatterySocPercent: isCurrentHour ? currentSoc : Math.min(100, Math.max(20, Math.floor(40 + solar * 12 - load * 5))),
      });
    }
    return data;
  };

  const chartData = generate24HourData();

  // Calculated live values with micro-jitter
  const liveSolar = Math.max(0, Number((metrics.currentPowerGenerationKw + (simulatedCloud ? -0.8 : jitter)).toFixed(2)));
  const liveLoad = Math.max(0.2, Number(((activeApplianceWatts / 1000) + (simulatedSurge ? 2.4 : jitter * 0.2)).toFixed(2)));
  const inverterLoadPct = Math.min(100, Math.round((liveLoad / preset.inverterCapacityKw) * 100));
  const liveVoltage = (230.4 + (simulatedSurge ? -1.8 : jitter * 1.5)).toFixed(1);
  const liveFreq = (50.01 + jitter * 0.08).toFixed(2);
  const liveTemp = (36.5 + (liveLoad > 3 ? 4.5 : 0) + jitter * 0.4).toFixed(1);

  // Generate Oscilloscope SVG Path Points (Pure Sine Wave)
  const generateSinePath = () => {
    const width = 280;
    const height = 50;
    const amplitude = simulatedSurge ? 22 : 18;
    const frequency = 0.08;
    let path = `M 0 ${height / 2}`;

    for (let x = 0; x <= width; x += 4) {
      const y = height / 2 + Math.sin(x * frequency + sinePhase) * amplitude;
      path += ` L ${x} ${y}`;
    }
    return path;
  };

  return (
    <div className="w-full max-w-none py-4 sm:py-10 space-y-8 text-white">
      {/* Top Banner & Heading */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider shadow-inner">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          Live IoT MQTT Stream • ID: AT-INV-UMUAHIA-09
        </div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tight text-white">
          Real-Time Cloud IoT Telemetry & Inverter Hub
        </h2>
        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
          Monitor instantaneous power generation, pure sine wave AC output, MPPT string efficiency, and battery state of charge in real time. Interact with the live appliance switchboard and environmental stress controls.
        </p>
      </div>

      {/* Control Bar: Live Auto-Stream, Speed, and Financial Ticker */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-slate-800/80">
          {/* Stream Status */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 shrink-0">
              <Radio className="w-6 h-6 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-white">Active IoT Gateway Stream</h3>
                <span className="bg-emerald-500/20 text-emerald-400 text-[10px] font-mono px-2 py-0.5 rounded border border-emerald-500/40">
                  WSS://IOT.ACCESSTECH.NG
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 mt-1 font-mono">
                <span>Latency: <strong className="text-emerald-400">{latency}ms</strong></span>
                <span>Packets: <strong className="text-cyan-400">{packetCount}</strong></span>
                <span>Data: <strong className="text-amber-400">{(bytesReceived / 1024).toFixed(1)} KB</strong></span>
                <span>Protocol: <strong className="text-purple-400">MQTT v5.0 TLS</strong></span>
              </div>
            </div>
          </div>

          {/* Live Financial Savings Ticker */}
          <div className="bg-slate-950 border border-emerald-500/30 px-5 py-3 rounded-2xl flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-black">
              ₦
            </div>
            <div>
              <div className="text-[10px] text-slate-400 uppercase font-semibold tracking-wider">Live Diesel Fuel Savings</div>
              <div className="text-lg font-black text-emerald-400 font-mono">
                ₦{sessionNairaSaved.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>

          {/* Interactive Play/Pause & Speed Controls */}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setIsLiveStreaming(!isLiveStreaming)}
              className={`px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 transition-all shadow-lg ${
                isLiveStreaming
                  ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30'
                  : 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'
              }`}
            >
              {isLiveStreaming ? (
                <>
                  <Pause className="w-4 h-4 fill-current" /> Pause Live Stream
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 fill-current" /> Resume Live Stream
                </>
              )}
            </button>

            <div className="flex items-center gap-1 bg-slate-950 p-1.5 rounded-xl border border-slate-800 text-xs font-mono">
              <span className="text-slate-500 px-2 font-sans font-semibold text-[11px]">Speed:</span>
              {[1, 2, 5, 10].map((s) => (
                <button
                  key={s}
                  onClick={() => setSpeedMultiplier(s)}
                  className={`px-2.5 py-1 rounded-lg font-bold transition-colors ${
                    speedMultiplier === s
                      ? 'bg-emerald-500 text-slate-950'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {s}x
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                setTimeOfDay(12);
                setSimulatedCloud(false);
                setSimulatedSurge(false);
                if (!gridAvailable) toggleGrid();
                addLog(`[SYSTEM] Simulation parameters reset to noon nominal defaults.`, 'INFO');
              }}
              className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 transition-colors"
              title="Reset Simulation"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 24-Hour Timeline Scrubbing Bar */}
        <div className="space-y-3">
          <div className="flex justify-between items-center text-xs">
            <span className="text-slate-400 font-medium flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-emerald-400" /> Simulated Solar Day Cycle:
              <strong className="text-emerald-400 font-mono text-sm ml-1">
                {Math.floor(timeOfDay).toString().padStart(2, '0')}:
                {Math.floor((timeOfDay % 1) * 60).toString().padStart(2, '0')} hrs
              </strong>
            </span>
            <span className="text-slate-500 text-[11px]">
              {timeOfDay >= 6 && timeOfDay <= 18 ? '☀️ Solar Harvesting Window' : '🌙 Night Battery Discharge Window'}
            </span>
          </div>

          <input
            type="range"
            min="0"
            max="24"
            step="0.1"
            value={timeOfDay}
            onChange={(e) => {
              setIsLiveStreaming(false);
              setTimeOfDay(Number(e.target.value));
            }}
            className="w-full accent-emerald-500 cursor-pointer h-2.5 bg-slate-950 rounded-lg"
          />

          <div className="flex justify-between text-[11px] text-slate-500 font-mono">
            <span>00:00 (Midnight)</span>
            <span>06:00 (Sunrise)</span>
            <span>12:00 (Peak Noon)</span>
            <span>18:00 (Sunset)</span>
            <span>24:00 (Midnight)</span>
          </div>
        </div>
      </div>

      {/* NEW: Pure Sine Wave AC Oscilloscope & Live Inverter Gauges */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Oscilloscope pure sine wave monitor */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Waves className="w-5 h-5 text-emerald-400 animate-pulse" />
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">230V Pure Sine Wave Monitor</h3>
            </div>
            <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/30">
              THD: 0.8%
            </span>
          </div>

          {/* SVG Waveform Box */}
          <div className="bg-slate-950 border border-slate-800/80 rounded-2xl p-4 relative overflow-hidden flex flex-col items-center justify-center">
            {/* Grid overlay lines */}
            <div className="absolute inset-0 grid grid-cols-6 grid-rows-4 opacity-15 pointer-events-none">
              <div className="border-r border-b border-emerald-500"></div>
              <div className="border-r border-b border-emerald-500"></div>
              <div className="border-r border-b border-emerald-500"></div>
              <div className="border-r border-b border-emerald-500"></div>
              <div className="border-r border-b border-emerald-500"></div>
              <div className="border-b border-emerald-500"></div>
            </div>

            <svg viewBox="0 0 280 50" className="w-full h-16 z-10">
              <path
                d={generateSinePath()}
                fill="none"
                stroke={simulatedSurge ? "#38bdf8" : "#10b981"}
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>

            <div className="w-full flex justify-between text-[11px] font-mono text-slate-400 mt-2 z-10">
              <span>V_RMS: <strong className="text-emerald-400">{liveVoltage}V</strong></span>
              <span>FREQ: <strong className="text-cyan-400">{liveFreq}Hz</strong></span>
              <span>PHASE: <strong className="text-amber-400">0.0°</strong></span>
            </div>
          </div>

          <div className="text-[11px] text-slate-400 flex items-center justify-between">
            <span>Inverter Waveform: <strong>Clean Pure Sine</strong></span>
            <span className="text-emerald-400 font-semibold">Zero Distortion</span>
          </div>
        </div>

        {/* Inverter Load Gauge Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Inverter Utilization Load %</span>
            <Gauge className="w-5 h-5 text-teal-400" />
          </div>

          <div className="flex items-center justify-around">
            {/* Radial Gauge */}
            <div className="relative w-28 h-28 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="56"
                  cy="56"
                  r="44"
                  stroke="#1e293b"
                  strokeWidth="8"
                  fill="transparent"
                />
                <circle
                  cx="56"
                  cy="56"
                  r="44"
                  stroke={inverterLoadPct > 85 ? "#f43f5e" : inverterLoadPct > 60 ? "#f59e0b" : "#14b8a6"}
                  strokeWidth="8"
                  strokeDasharray={276}
                  strokeDashoffset={276 - (276 * inverterLoadPct) / 100}
                  strokeLinecap="round"
                  fill="transparent"
                  className="transition-all duration-500"
                />
              </svg>
              <div className="absolute text-center">
                <div className="text-xl font-black text-white font-mono">{inverterLoadPct}%</div>
                <div className="text-[9px] text-slate-400 uppercase">LOAD</div>
              </div>
            </div>

            <div className="space-y-1.5 text-xs font-mono">
              <div>Current: <strong className="text-teal-400">{liveLoad} kW</strong></div>
              <div>Capacity: <strong className="text-white">{preset.inverterCapacityKw} kVA</strong></div>
              <div>Surge Max: <strong className="text-amber-400">{preset.inverterCapacityKw * 2} kVA</strong></div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
            <span>Thermal Fan: <strong>Auto Variable</strong></span>
            <span className={inverterLoadPct > 80 ? 'text-amber-400 font-bold' : 'text-emerald-400 font-bold'}>
              {inverterLoadPct > 80 ? 'HIGH LOAD' : 'NOMINAL'}
            </span>
          </div>
        </div>

        {/* MPPT Solar Harvesting Efficiency Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">MPPT Solar String Efficiency</span>
            <Sun className="w-5 h-5 text-amber-400 animate-spin-slow" />
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="text-2xl font-black text-amber-400 font-mono">{liveSolar} kW</span>
              <span className="text-xs text-slate-400">Peak Array: {preset.solarPanelCapacityKw} kWp</span>
            </div>

            {/* Simulated Live Equalizer Bar */}
            <div className="flex items-end gap-1.5 h-12 pt-2">
              {[40, 65, 80, 95, 100, 85, 70, 90, 60, 45, 30, 85].map((val, idx) => (
                <div
                  key={idx}
                  className="flex-1 bg-gradient-to-t from-amber-500/20 to-amber-400 rounded-t transition-all duration-300"
                  style={{
                    height: `${liveSolar > 0 ? (val * (liveSolar / preset.solarPanelCapacityKw)) : 5}%`
                  }}
                />
              ))}
            </div>
          </div>

          <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400 font-mono">
            <span>MPPT Tracker 1: <strong>Active</strong></span>
            <span className="text-amber-400 font-bold">98.4% Efficiency</span>
          </div>
        </div>
      </div>

      {/* NEW: Interactive Live Appliance Switchboard */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-xs font-bold uppercase tracking-wider mb-2">
              <Power className="w-3.5 h-3.5" /> Interactive Load Control Panel
            </div>
            <h3 className="text-xl font-bold text-white">
              Live Household Appliance Switchboard
            </h3>
            <p className="text-xs text-slate-400">
              Toggle appliances on/off to see how household power demand, inverter load %, and battery runtime shift in real time.
            </p>
          </div>

          <div className="bg-slate-950 border border-slate-800 px-4 py-2.5 rounded-2xl flex items-center gap-3">
            <span className="text-xs text-slate-400 font-medium">Switchboard Load:</span>
            <span className="text-lg font-black text-cyan-400 font-mono">
              {activeApplianceWatts} W
            </span>
          </div>
        </div>

        {/* Appliance Grid Buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {appliances.map((app) => (
            <button
              key={app.id}
              onClick={() => toggleAppliance(app.id)}
              className={`p-4 rounded-2xl border flex flex-col justify-between gap-3 text-left transition-all relative overflow-hidden group ${
                app.active
                  ? 'bg-slate-950 border-cyan-500/50 shadow-lg shadow-cyan-500/10'
                  : 'bg-slate-950/60 border-slate-800 opacity-60 hover:opacity-80'
              }`}
            >
              <div className="flex justify-between items-center">
                <div className={`p-2 rounded-xl ${app.active ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800 text-slate-500'}`}>
                  {app.icon}
                </div>
                <div className={`w-3 h-3 rounded-full ${app.active ? 'bg-cyan-400 animate-pulse' : 'bg-slate-800'}`} />
              </div>

              <div>
                <div className="text-xs font-bold text-white group-hover:text-cyan-300 transition-colors">
                  {app.name}
                </div>
                <div className="text-[11px] font-mono text-cyan-400 font-semibold mt-0.5">
                  {app.watts} Watts
                </div>
              </div>

              <div className="text-[10px] text-slate-500 uppercase font-semibold">
                {app.category}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Primary 24-Hour Real-Time Power Graph */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-400" />
              24-Hour Generation vs Load Demand Profile
            </h3>
            <p className="text-xs text-slate-400">
              Real-time trend analysis comparing solar production (kW) against household appliance consumption (kW).
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400">
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-emerald-500 inline-block"></span> Solar Harvest
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-full bg-teal-400 inline-block"></span> Household Load
            </span>
          </div>
        </div>

        <div className="h-72 w-full pt-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorSolar" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                </linearGradient>
                <linearGradient id="colorLoad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="time" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} unit="kW" />
              <Tooltip
                contentStyle={{ backgroundColor: '#090d16', borderColor: '#334155', borderRadius: '12px', fontSize: '12px' }}
                itemStyle={{ color: '#34d399' }}
              />
              <Area type="monotone" dataKey="SolarGenerationKw" name="Solar Harvest (kW)" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorSolar)" />
              <Area type="monotone" dataKey="LoadDemandKw" name="Load Demand (kW)" stroke="#2dd4bf" strokeWidth={2} fillOpacity={1} fill="url(#colorLoad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Stress Testing & Terminal Logs Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Environmental & Load Stress Testing Controls */}
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-6 shadow-2xl">
          <div className="space-y-1">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <Gauge className="w-5 h-5 text-amber-400" /> Live Physical Stress Testing Controls
            </h3>
            <p className="text-xs text-slate-400">
              Trigger simulated environmental shocks to observe how the solar physics engine automatically compensates.
            </p>
          </div>

          <div className="space-y-4">
            {/* Stress Button 1: Grid Disconnect */}
            <button
              onClick={() => toggleGrid()}
              className={`w-full p-4 rounded-2xl border text-left flex items-center justify-between transition-all ${
                !gridAvailable
                  ? 'bg-rose-500/10 border-rose-500/50 text-rose-300'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 ${!gridAvailable ? 'text-rose-400 animate-bounce' : 'text-slate-500'}`} />
                <div>
                  <div className="font-bold text-sm">Simulate Grid Blackout (Discos Cut)</div>
                  <div className="text-xs text-slate-400">Tests 0ms UPS seamless transfer without light flickering.</div>
                </div>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${!gridAvailable ? 'bg-rose-500 text-white' : 'bg-slate-800 text-slate-400'}`}>
                {!gridAvailable ? 'BLACKOUT ACTIVE' : 'GRID ONLINE'}
              </span>
            </button>

            {/* Stress Button 2: Cloud Cover */}
            <button
              onClick={() => setSimulatedCloud(!simulatedCloud)}
              className={`w-full p-4 rounded-2xl border text-left flex items-center justify-between transition-all ${
                simulatedCloud
                  ? 'bg-amber-500/10 border-amber-500/50 text-amber-300'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <Sun className={`w-5 h-5 ${simulatedCloud ? 'text-amber-400' : 'text-slate-500'}`} />
                <div>
                  <div className="font-bold text-sm">Simulate Cloud Shadow (45% Solar Drop)</div>
                  <div className="text-xs text-slate-400">Observe hybrid battery discharge compensating for solar drop.</div>
                </div>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${simulatedCloud ? 'bg-amber-500 text-slate-950' : 'bg-slate-800 text-slate-400'}`}>
                {simulatedCloud ? 'SHADOW ACTIVE' : 'CLEAR SKY'}
              </span>
            </button>

            {/* Stress Button 3: AC Surge */}
            <button
              onClick={() => setSimulatedSurge(!simulatedSurge)}
              className={`w-full p-4 rounded-2xl border text-left flex items-center justify-between transition-all ${
                simulatedSurge
                  ? 'bg-cyan-500/10 border-cyan-500/50 text-cyan-300'
                  : 'bg-slate-950 border-slate-800 text-slate-300 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center gap-3">
                <Flame className={`w-5 h-5 ${simulatedSurge ? 'text-cyan-400 animate-pulse' : 'text-slate-500'}`} />
                <div>
                  <div className="font-bold text-sm">Simulate Heavy AC Compressor Surge</div>
                  <div className="text-xs text-slate-400">Tests 2x surge kVA rating of Pure Sine Wave inverters.</div>
                </div>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full ${simulatedSurge ? 'bg-cyan-500 text-slate-950' : 'bg-slate-800 text-slate-400'}`}>
                {simulatedSurge ? 'SURGE ACTIVE' : 'NORMAL LOAD'}
              </span>
            </button>
          </div>
        </div>

        {/* Real-time MQTT Live Terminal Logs */}
        <div className="bg-slate-950 border border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-2xl flex flex-col justify-between font-mono">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-emerald-400 flex items-center gap-2 font-sans">
                <Terminal className="w-4 h-4 text-emerald-400" />
                Live Telemetry Terminal Console
              </h3>
              <span className="text-[10px] text-slate-500 bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                MQTT TOPIC: /accesstech/telemetry/v1
              </span>
            </div>
            <p className="text-xs text-slate-400 font-sans">
              Real-time event packet log broadcast from the hybrid inverter's Wi-Fi / RS485 cloud module.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 h-64 overflow-y-auto space-y-2 text-[11px] leading-relaxed scrollbar-thin scrollbar-thumb-slate-800">
            {logs.map((log) => (
              <div key={log.id} className="flex gap-2">
                <span className="text-slate-500 shrink-0">[{log.timestamp}]</span>
                <span className={log.type === 'WARN' ? 'text-amber-400' : log.type === 'EVENT' ? 'text-cyan-400' : 'text-emerald-400'}>
                  {log.message}
                </span>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-slate-900 flex items-center justify-between text-[11px] text-slate-500 font-sans">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Cloud Sync Status: OK
            </span>
            <span>Firmware: v4.8.2-PROD</span>
          </div>
        </div>
      </div>
    </div>
  );
};
